<template>
  <div
    v-if="canShowWidget"
    :class="[
      'newsletter-subscription-widget max-w-2xl flex flex-row items-stretch rounded-l-md overflow-hidden shadow-lg',
      'fixed top-1/2 right-0 -translate-y-1/2 z-50'
    ]"
  >
    <div
      :class="[
        'flex flex-col gap-y-5 py-5 w-10 min-w-[40px] cursor-pointer shadow-lg',
        'bg-EXPONDO-secondary text-EXPONDO-white'
      ]"
      @click="toggleRight"
    >
      <div class="flex self-center items-center text-EXPONDO-white text-xl leading-5">
        <span
          v-if="rightSideLoading"
          class="iconify fa-pro-solid--circle-notch animate-spin"
        />
        <CloseButton
          v-else
          icon-color-class="text-EXPONDO-white"
          @click.stop="handleCloseClick"
        />
      </div>
      <div class="flex items-end justify-center flex-auto">
        <span
          :class="[
            'inline-block text-EXPONDO-white whitespace-nowrap p-0 text-sm font-bold select-none',
            'rotate-180 [writing-mode:vertical-lr] [text-orientation:sideways]',
          ]"
        >
          <i18n-t
          keypath="XX for YOU">
            <template #voucherValue>
              <span>{{ voucherValue }}</span>
            </template>
          </i18n-t>
        </span>
      </div>
    </div>

    <div
      v-if="showRight"
      class="bg-EXPONDO-grey-5"
    >
      <NewsletterForm
        :signup-button-text="$t('SIGN ME UP')"
        :newsletter-action-type="NEWSLETTER_ACTION_WITH_VOUCHER"
        :input-id="inputId"
        :is-subscription-widget="true"
        source="discount-codes-page"
        @subscription-progress-update="onSubscriptionProgressUpdated"
      >
        <template #header>
          <i18n-t
          keypath="DO YOU WANT A XX DISCOUNT?"
          tag="h3"
          class="text-2xl leading-7 text-EXPONDO-grey">
            <template #voucherValue>
              <span class="text-EXPONDO-secondary">{{ voucherValue }}</span>
            </template>
          </i18n-t>
          <p class="text-xs text-EXPONDO-grey mt-0.5 mb-2.5">
            <i18n-t
            keypath="*suitable for a minimum basket value of XX">
              <template #minimumPurchaseValue>
                <span>{{ minimumPurchaseValue }}</span>
              </template>
            </i18n-t>
          </p>
          <p class="text-base text-EXPONDO-grey mb-5">
            {{ $t('Sign up to our newsletter and receive the discount code!') }}
          </p>
        </template>

        <template #error-details="{ error }">
          <p class="text-EXPONDO-red">
            {{ error }}
          </p>
        </template>

        <template #newsletter-error>
          <p class="text-EXPONDO-red text-sm">
            {{ $t('newsletterError') }}
          </p>
        </template>

        <template #thank-you>
          <h3 class="text-2xl leading-7 mb-2 text-EXPONDO-grey text-center break-all max-w-max mx-auto">
            <i18n-t
            keypath="Hey customer,">
              <template #customerMaskedEmail>
                <span>{{ subscriptionProgressData.emailMasked }}</span>
              </template>
            </i18n-t>
          </h3>
          <ImgView
            src="/newsletter/newsletter-mobile.png"
            width="160"
            :alt="$t('It’s time to GET IT DONE with expondo.').toString()"
            img-classes="mx-auto w-auto my-4 text-center"
          />
          <p class="text-sm text-EXPONDO-grey mb-2 text-center">
            <i18n-t
            keypath="Confirm your newsletter subscription via the email we sent to receive your XX voucher.">
              <template #voucherValue>
                <span>{{ voucherValue }}</span>
              </template>
            </i18n-t>
          </p>
          <p class="text-sm text-EXPONDO-grey mb-2 text-center">
            {{ $t('It’s time to GET IT DONE with expondo.') }}
          </p>
        </template>

        <template #privacy>
          <button
            class="my-2 text-base underline text-EXPONDO-secondary cursor-pointer text-left"
            @click="toggleRight"
          >
            {{ $t('I don’t want discounts or exclusive content') }}
          </button>

          <i18n-t
          keypath="Newsletter floating widget privacy policy"
          tag="p"
          class="text-xs">
            <template #privacyPolicy>
              <CustomLink
                :text="$t('Newsletter Privacy Policy')"
                :url="privacySlug"
                :type="LINK_TYPE.NoStyle"
                class="underline"
              />
            </template>
          </i18n-t>
        </template>
      </NewsletterForm>
    </div>
  </div>
</template>

<script lang="ts">


import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import { NEWSLETTER_ACTION_WITH_VOUCHER } from '~~/constants/cia';
import { ROUTES } from '~~/constants/routes';
import type { NewsletterSubscriptionData } from '~~/types/product/NewsletterSubscriptionData';

export default defineComponent({
  name: 'NewsletterSubscriptionWidget',

  props: {
    inputId: {
      type: String,
      default: 'newsletter-widget-input',
    },
  },
  setup() {

    const subscriptionProgressData = ref(null);
    const canRenderWidget = ref(false);
    const showRight = ref(false);
    const {
      voucherValue,
      minimumPurchaseValue,
      canRenderSubscriptionWidget,
      postponeWidgetDisplay,
    } = useNewsletterVoucher();
    const { getAdjustedSlug } = useRouteExtended();
    const { isCheckoutPage, isOnNewsletterConfirmationPage, isLoginPage } = useIsPage();

    const privacySlug = computed(() => getAdjustedSlug(ROUTES.PRIVACY));

    const toggleRight = () => {

      if (showRight.value === false) {

        subscriptionProgressData.value = null;

      }
      showRight.value = !showRight.value;

    };

    const closeCompletely = () => {

      postponeWidgetDisplay();
      canRenderWidget.value = false;

    };

    const handleCloseClick = () => {

      showRight.value ? showRight.value = false : closeCompletely();

    };

    const isNotOnProhibitedPage = computed(() => {

      return !isCheckoutPage.value && !isOnNewsletterConfirmationPage.value && !isLoginPage.value;

    });

    const checkVisibility = async () => {

      canRenderWidget.value = await canRenderSubscriptionWidget();

    };

    const onSubscriptionProgressUpdated = (value: NewsletterSubscriptionData) => {

      subscriptionProgressData.value = value;

    };

    const canShowWidget = computed(() => {

      return canRenderWidget.value && isNotOnProhibitedPage.value;

    });

    const rightSideLoading = computed(() => {

      return subscriptionProgressData.value === null && showRight.value;

    });

    onMounted(async () => {

      await checkVisibility();

    });

    return {
      canShowWidget,
      showRight,
      toggleRight,
      rightSideLoading,
      handleCloseClick,
      NEWSLETTER_ACTION_WITH_VOUCHER,
      voucherValue,
      minimumPurchaseValue,
      LINK_TYPE,
      privacySlug,
      subscriptionProgressData,
      onSubscriptionProgressUpdated,
    };

  },
});

</script>
